import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../services/api';
import { useAuth } from '../../store/AuthContext';
import { Settings, Clock, Save, Loader2, AlertTriangle, ShieldCheck, Activity, Database, CheckCircle, XCircle, RefreshCw, Trash2, ShieldOff, Brain, Radar } from 'lucide-react';

const AdminSettings = () => {
  const { token } = useAuth();
  const [tokenExpiry, setTokenExpiry] = useState('24h');
  const [openaiKey, setOpenaiKey] = useState('');
  const [geminiKey, setGeminiKey] = useState('');
  const [googleMapsKey, setGoogleMapsKey] = useState('');
  const [deepNexusKey, setDeepNexusKey] = useState('');
  const [aiRefreshDays, setAiRefreshDays] = useState('7');
  const [maxRetries, setMaxRetries] = useState('3');
  const [scoutScanRadius, setScoutScanRadius] = useState('500');
  const [scoutSafeArea, setScoutSafeArea] = useState('300');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [message, setMessage] = useState(null);

  const fetchSettings = async () => {
    try {
      const resp = await axios.get(`${getApiUrl()}/setup`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const settings = resp.data.data;
      
      const expirySetting = settings.find(s => s.key === 'token_expiry');
      if (expirySetting) setTokenExpiry(expirySetting.value);
      
      const openaiSetting = settings.find(s => s.key === 'openai_api_key');
      if (openaiSetting) setOpenaiKey(openaiSetting.value);
      
      const geminiSetting = settings.find(s => s.key === 'gemini_api_key');
      if (geminiSetting) setGeminiKey(geminiSetting.value);
      
      const googleMapsSetting = settings.find(s => s.key === 'google_maps_api_key');
      if (googleMapsSetting) setGoogleMapsKey(googleMapsSetting.value);

      const deepNexusSetting = settings.find(s => s.key === 'deepnexus_api_key');
      if (deepNexusSetting) setDeepNexusKey(deepNexusSetting.value);

      const aiRefreshSetting = settings.find(s => s.key === 'ai_refresh_days');
      if (aiRefreshSetting) setAiRefreshDays(aiRefreshSetting.value);

      const maxRetriesSetting = settings.find(s => s.key === 'max_download_retries');
      if (maxRetriesSetting) setMaxRetries(maxRetriesSetting.value);

      const scoutRadiusSetting = settings.find(s => s.key === 'scout_scan_radius');
      if (scoutRadiusSetting) setScoutScanRadius(scoutRadiusSetting.value);

      const scoutSafeAreaSetting = settings.find(s => s.key === 'scout_safe_area');
      if (scoutSafeAreaSetting) setScoutSafeArea(scoutSafeAreaSetting.value);
      
    } catch (err) {
      console.error('Failed to fetch mission settings', err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      // Save all settings
      await Promise.all([
        axios.post(`${getApiUrl()}/setup`, { key: 'token_expiry', value: tokenExpiry }, { headers: { Authorization: `Bearer ${token}` } }),
        openaiKey && axios.post(`${getApiUrl()}/setup`, { key: 'openai_api_key', value: openaiKey }, { headers: { Authorization: `Bearer ${token}` } }),
        geminiKey && axios.post(`${getApiUrl()}/setup`, { key: 'gemini_api_key', value: geminiKey }, { headers: { Authorization: `Bearer ${token}` } }),
        googleMapsKey && axios.post(`${getApiUrl()}/setup`, { key: 'google_maps_api_key', value: googleMapsKey }, { headers: { Authorization: `Bearer ${token}` } }),
        deepNexusKey && axios.post(`${getApiUrl()}/setup`, { key: 'deepnexus_api_key', value: deepNexusKey }, { headers: { Authorization: `Bearer ${token}` } }),
        axios.post(`${getApiUrl()}/setup`, { key: 'ai_refresh_days', value: aiRefreshDays }, { headers: { Authorization: `Bearer ${token}` } }),
        axios.post(`${getApiUrl()}/setup`, { key: 'max_download_retries', value: maxRetries }, { headers: { Authorization: `Bearer ${token}` } }),
        axios.post(`${getApiUrl()}/setup`, { key: 'scout_scan_radius', value: scoutScanRadius }, { headers: { Authorization: `Bearer ${token}` } }),
        axios.post(`${getApiUrl()}/setup`, { key: 'scout_safe_area', value: scoutSafeArea }, { headers: { Authorization: `Bearer ${token}` } })
      ].filter(Boolean));
      
      setMessage({ type: 'success', text: 'MISSION PARAMETERS UPDATED SUCCESSFULLY' });
    } catch (err) {
      setMessage({ type: 'error', text: 'FAILED TO UPDATE MISSION PARAMETERS' });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div style={{ color: '#0B3D91', padding: '20px' }}>ACCESSING CENTRAL CONFIGURATION...</div>;

  return (
    <div style={{ maxWidth: '800px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '30px' }}>
        <Settings size={28} color="#0B3D91" />
        <h1 style={{ margin: 0, letterSpacing: '2px', textTransform: 'uppercase' }}>System Configuration</h1>
      </div>

      <div style={{ 
        background: 'rgba(5, 7, 10, 0.9)', 
        border: '1px solid #0B3D91', 
        padding: '30px',
        borderRadius: '4px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px', color: '#00CEC9' }}>
          <ShieldCheck size={20} />
          <span style={{ fontSize: '0.8rem', fontWeight: 'bold', letterSpacing: '1px' }}>SECURITY PROTOCOLS</span>
        </div>

        <form onSubmit={handleSave}>
          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#888', fontSize: '0.7rem', marginBottom: '10px', letterSpacing: '1px' }}>
              <Clock size={14} /> TOKEN LIFESPAN (JWT EXPIRE)
            </label>
            <select
              className="nasa-input"
              value={tokenExpiry}
              onChange={(e) => setTokenExpiry(e.target.value)}
              style={{ maxWidth: '300px' }}
            >
              <option value="1h">1 HOUR (High Security)</option>
              <option value="12h">12 HOURS (Standard Shift)</option>
              <option value="24h">24 HOURS (Default Mission)</option>
              <option value="7d">7 DAYS (Extended Patrol)</option>
              <option value="30d">30 DAYS (Deep Space)</option>
            </select>
            <p style={{ fontSize: '0.65rem', color: '#555', marginTop: '8px', fontStyle: 'italic' }}>
              Controls how long a personnel session remains active before requiring re-authentication.
            </p>
          </div>

          <div style={{ 
            backgroundColor: 'rgba(0, 206, 201, 0.05)', 
            border: '1px solid rgba(0, 206, 201, 0.3)', 
            padding: '20px', 
            marginBottom: '25px'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '0.8rem', color: '#00CEC9', letterSpacing: '1px' }}>
              🔑 API KEYS MANAGEMENT
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', marginBottom: '8px', letterSpacing: '1px' }}>
                OPENAI API KEY
              </label>
              <input
                type="password"
                className="nasa-input"
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
                placeholder="sk-..."
              />
              <p style={{ fontSize: '0.65rem', color: '#555', marginTop: '5px', fontStyle: 'italic' }}>
                Used for AI-powered market analysis (competence & engines detection)
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', marginBottom: '8px', letterSpacing: '1px' }}>
                GEMINI API KEY
              </label>
              <input
                type="password"
                className="nasa-input"
                value={geminiKey}
                onChange={(e) => setGeminiKey(e.target.value)}
                placeholder="AI..."
              />
              <p style={{ fontSize: '0.65rem', color: '#555', marginTop: '5px', fontStyle: 'italic' }}>
                Google Gemini AI integration
              </p>
            </div>

            <div style={{ marginBottom: '0' }}>
              <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', marginBottom: '8px', letterSpacing: '1px' }}>
                GOOGLE MAPS API KEY
              </label>
              <input
                type="password"
                className="nasa-input"
                value={googleMapsKey}
                onChange={(e) => setGoogleMapsKey(e.target.value)}
                placeholder="AIza..."
              />
              <p style={{ fontSize: '0.65rem', color: '#555', marginTop: '5px', fontStyle: 'italic' }}>
                Required for maps rendering and geocoding services
              </p>
            </div>

            <div style={{ marginTop: '20px' }}>
              <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', marginBottom: '8px', letterSpacing: '1px' }}>
                DEEPNEXUS API KEY
              </label>
              <input
                type="password"
                className="nasa-input"
                value={deepNexusKey}
                onChange={(e) => setDeepNexusKey(e.target.value)}
                placeholder="dnx_..."
              />
              <p style={{ fontSize: '0.65rem', color: '#555', marginTop: '5px', fontStyle: 'italic' }}>
                API key para DeepNexus (catastro y registro de la propiedad)
              </p>
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'rgba(11, 61, 145, 0.1)', 
            border: '1px solid rgba(11, 61, 145, 0.3)', 
            padding: '20px', 
            marginBottom: '25px'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '0.8rem', color: '#00CEC9', letterSpacing: '1px' }}>
              🧠 AI INTEL SETTINGS
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '40px' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', letterSpacing: '1px' }}>
                    REFRESH INTERVAL (DAYS)
                  </label>
                  <span style={{ color: '#00CEC9', fontSize: '1rem', fontWeight: 'bold', fontFamily: 'monospace' }}>{aiRefreshDays}d</span>
                </div>
                <input
                  type="range"
                  style={{ width: '100%', accentColor: '#00CEC9', cursor: 'pointer' }}
                  value={aiRefreshDays}
                  onChange={(e) => setAiRefreshDays(e.target.value)}
                  min="1"
                  max="30"
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#444', marginTop: '5px' }}>
                  <span>1</span>
                  <span>15</span>
                  <span>30</span>
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', letterSpacing: '1px' }}>
                    MAX DOWNLOAD RETRIES
                  </label>
                  <span style={{ color: '#00CEC9', fontSize: '1rem', fontWeight: 'bold', fontFamily: 'monospace' }}>{maxRetries}x</span>
                </div>
                <input
                  type="range"
                  style={{ width: '100%', accentColor: '#00CEC9', cursor: 'pointer' }}
                  value={maxRetries}
                  onChange={(e) => setMaxRetries(e.target.value)}
                  min="1"
                  max="30"
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#444', marginTop: '5px' }}>
                  <span>1</span>
                  <span>15</span>
                  <span>30</span>
                </div>
              </div>
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'rgba(226, 123, 88, 0.08)', 
            border: '1px solid rgba(226, 123, 88, 0.4)', 
            padding: '20px', 
            marginBottom: '25px'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '0.8rem', color: '#E27B58', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Radar size={16} /> SCOUT PARAMS.
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '40px' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', letterSpacing: '1px' }}>
                    MAX SCAN RADIUS (METERS)
                  </label>
                  <span style={{ color: '#E27B58', fontSize: '1rem', fontWeight: 'bold', fontFamily: 'monospace' }}>{scoutScanRadius}m</span>
                </div>
                <input
                  type="range"
                  style={{ width: '100%', accentColor: '#E27B58', cursor: 'pointer' }}
                  value={scoutScanRadius}
                  onChange={(e) => setScoutScanRadius(e.target.value)}
                  min="100"
                  max="50000"
                  step="50"
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#444', marginTop: '5px' }}>
                  <span>100m</span>
                  <span>25km</span>
                  <span>50km</span>
                </div>
                <p style={{ fontSize: '0.65rem', color: '#555', marginTop: '8px', fontStyle: 'italic' }}>
                  Radio máximo al buscar competencia y motores en el análisis de viabilidad.
                </p>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label style={{ display: 'block', color: '#888', fontSize: '0.7rem', letterSpacing: '1px' }}>
                    MAX SAFE AREA (METERS)
                  </label>
                  <span style={{ color: '#E27B58', fontSize: '1rem', fontWeight: 'bold', fontFamily: 'monospace' }}>{scoutSafeArea}m</span>
                </div>
                <input
                  type="range"
                  style={{ width: '100%', accentColor: '#E27B58', cursor: 'pointer' }}
                  value={scoutSafeArea}
                  onChange={(e) => setScoutSafeArea(e.target.value)}
                  min="50"
                  max="50000"
                  step="50"
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#444', marginTop: '5px' }}>
                  <span>50m</span>
                  <span>25km</span>
                  <span>50km</span>
                </div>
                <p style={{ fontSize: '0.65rem', color: '#555', marginTop: '8px', fontStyle: 'italic' }}>
                  Zona de exclusión alrededor de locales propios para evitar canibalización.
                </p>
              </div>
            </div>
          </div>

          <div style={{ 
            backgroundColor: 'rgba(226, 123, 88, 0.05)', 
            border: '1px solid rgba(226, 123, 88, 0.3)', 
            padding: '15px', 
            marginBottom: '30px',
            display: 'flex',
            gap: '15px',
            alignItems: 'flex-start'
          }}>
            <AlertTriangle size={20} color="#E27B58" style={{ flexShrink: 0 }} />
            <p style={{ margin: 0, fontSize: '0.75rem', color: '#E27B58', lineHeight: '1.4' }}>
              <strong>ATTENTION:</strong> Changes to token lifespan will only affect <strong>new sessions</strong>. Current active sessions will retain their original expiry parameters until they expire or the user logs out.
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="nasa-button"
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            {loading ? 'SYNCHRONIZING...' : 'COMMIT CHANGES TO CORE'}
          </button>
        </form>

        {message && (
          <div style={{
            marginTop: '20px',
            padding: '12px',
            fontSize: '0.8rem',
            backgroundColor: message.type === 'success' ? 'rgba(0, 184, 148, 0.1)' : 'rgba(214, 48, 49, 0.1)',
            color: message.type === 'success' ? '#00B894' : '#D63031',
            border: `1px solid ${message.type === 'success' ? '#00B894' : '#D63031'}`,
            textAlign: 'center'
          }}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
